/**
 * SortableSessionCard - Draggable wrapper for SessionCard
 *
 * Uses @dnd-kit/sortable for drag-and-drop functionality
 */

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { SessionCard } from './SessionCard'
import { ActionIcon, Box } from '@mantine/core'
import { IconGripVertical } from '@tabler/icons-react'
import type { SessionInfo } from '@session-tools/monitor-ui-core'

interface SortableSessionCardProps {
  id: string
  session: SessionInfo
  isMuted: boolean
  isInPool: boolean
  lastChangeTime?: number
  onToggleMute: () => void
  onUnmonitor: () => void
}

export function SortableSessionCard({
  id,
  session,
  isMuted,
  isInPool,
  lastChangeTime,
  onToggleMute,
  onUnmonitor
}: SortableSessionCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const
  }

  return (
    <Box ref={setNodeRef} style={style}>
      {/* Drag handle */}
      <ActionIcon
        {...attributes}
        {...listeners}
        variant="subtle"
        color="gray"
        size="sm"
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          cursor: 'grab',
          zIndex: 10
        }}
        title="Drag to reorder"
      >
        <IconGripVertical size={16} />
      </ActionIcon>

      <SessionCard
        session={session}
        isMuted={isMuted}
        isInPool={isInPool}
        lastChangeTime={lastChangeTime}
        onToggleMute={onToggleMute}
        onUnmonitor={onUnmonitor}
      />
    </Box>
  )
}
